import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Card,
    CardContent,
    Chip,
    Stack,
    Alert
} from "@mui/material";
import useAuth from "../../hooks/useAuth";
import teamService from "../../services/teamService";
import AdminLayout from "../AdminLayout";

export default function StudentTeamsPage() {
    const { user, userProfile } = useAuth();
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [joinOpen, setJoinOpen] = useState(false);
    const [teamName, setTeamName] = useState("");
    const [joinCode, setJoinCode] = useState("");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const canCreate = userProfile?.role === "student";

    const loadTeams = async () => {
        try {
            setLoading(true);
            setError("");
            const data = await teamService.getTeamsByUser(user?.uid);
            setTeams(data || []);
        } catch (e) {
            setError(e.message || "Failed to load teams");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.uid) {
            loadTeams();
        }
    }, [user?.uid]);

    const handleCreateTeam = async () => {
        try {
            setError("");
            setSuccess("");
            if (!teamName.trim()) {
                setError("Team name is required");
                return;
            }
            const created = await teamService.createStudentTeam(user.uid, teamName);
            setTeamName("");
            setCreateOpen(false);
            setSuccess(`Team created. Team code: ${created.teamCode}`);
            await loadTeams();
        } catch (e) {
            setError(e.message || "Failed to create team");
        }
    };

    const handleJoinTeam = async () => {
        try {
            setError("");
            setSuccess("");
            if (!joinCode.trim()) {
                setError("Team code is required");
                return;
            }
            const joined = await teamService.joinTeamByCode(user.uid, joinCode);
            setJoinCode("");
            setJoinOpen(false);
            setSuccess(`Joined team: ${joined.teamName}`);
            await loadTeams();
        } catch (e) {
            setError(e.message || "Failed to join team");
        }
    };

    return (
        <AdminLayout title="Teams">
            <Box sx={{ p: 3 }}>
                <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                            My Teams
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Create or join teams to collaborate on competitions, events and projects.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        {canCreate && (
                            <Button variant="contained" onClick={() => setCreateOpen(true)}>
                                Create Team
                            </Button>
                        )}
                        <Button variant="outlined" onClick={() => setJoinOpen(true)}>
                            Join Team
                        </Button>
                    </Stack>
                </Box>

                {error && (
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="error" onClose={() => setError("")}>
                            {error}
                        </Alert>
                    </Box>
                )}
                {success && (
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="success" onClose={() => setSuccess("")}>
                            {success}
                        </Alert>
                    </Box>
                )}

                {loading ? (
                    <Typography>Loading...</Typography>
                ) : teams.length === 0 ? (
                    <Box sx={{ textAlign: "center", mt: 8 }}>
                        <Typography variant="body1" color="text.secondary">
                            You are not part of any team yet.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {teams.map((team) => (
                            <Grid item xs={12} md={6} lg={4} key={team.id}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                            {team.teamName}
                                        </Typography>
                                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                                            <Chip size="small" label={team.teamType || "Student"} />
                                            {team.teamCode && (
                                                <Chip size="small" label={`Code: ${team.teamCode}`} />
                                            )}
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            Leader: {team.leaderName || (team.leaderId === user?.uid ? "You" : team.leaderId)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Leader Email: {team.leaderEmail || "-"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            College: {team.leaderCollege || "-"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Members: {Array.isArray(team.members) ? team.members.length : 0}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle>Create Team</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Team Name"
                            value={teamName}
                            onChange={(e) => setTeamName(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleCreateTeam}>
                            Create
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={joinOpen} onClose={() => setJoinOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle>Join Team</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Team Code"
                            value={joinCode}
                            onChange={(e) => setJoinCode(e.target.value)}
                            inputProps={{ style: { textTransform: "uppercase" } }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setJoinOpen(false)}>Cancel</Button>
                        <Button variant="contained" onClick={handleJoinTeam}>
                            Join
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </AdminLayout>
    );
}
